/**
 * Which titles the cover page prints, and in what order.
 *
 * There is no "CCTV Contract" title any more. Jaron struck it off Lisa
 * Warren's proposal on 2026-08-25 ("This is NOT a CCTV contract") and, asked
 * on 2026-08-31 whether the replacement applied to everyone in her situation,
 * answered wider than the question: "No, I want it to say Home Improvement for
 * everything including CCTV". Monday 12896823625.
 *
 * So a customer who really did buy CCTV is titled Home Improvement too. That
 * was 232 of the 786 customers carrying `Security`; the other 554 were being
 * titled for a product they never bought, which is what he was looking at.
 *
 * `security_cams` is no longer read here at all - which product was sold stops
 * mattering to the title once every one of them lands on the same one.
 *
 * "Everything" includes Solar sold on its own, which is why `Solar` appears
 * below. It had no title of any kind - the cover page rendered a 373px blank
 * where the heading belongs. 117 customers, measured across all 7,166 records
 * on 2026-09-03, and it is the ONLY combination this clause moves: Solar
 * always arrives beside HIL, Security or RC otherwise, and those already
 * supplied a heading. Monday 12941155218.
 */
export interface ReportTitles {
  /** Where every `Security` customer now lands, CCTV or not. */
  showHomeImprovement: boolean
  showRoofClaim: boolean
}

export const reportTitles = (
  currProducts: readonly string[],
): ReportTitles => ({
  showHomeImprovement:
    currProducts.includes('HIL') ||
    currProducts.includes('Security') ||
    currProducts.includes('Solar') ||
    currProducts.length === 0,
  showRoofClaim: currProducts.includes('RC'),
})

/**
 * Empty `products` means "everything" on a legacy deal, and also "not loaded
 * yet": a Roof Claim customer opened on "Home Improvement Contract" for a
 * moment (Jaron, Nov 4 video). No title until that is known (V102).
 */
export const reportTitlesKnown = (
  currProducts: readonly string[],
  productsResolved: boolean,
): boolean => currProducts.length > 0 || productsResolved

/**
 * A separator earns its place only when there is a title on BOTH sides of it.
 *
 * The old condition for the Roof Claim separator listed `Solar` directly,
 * which at the time printed no title on this page at all, so a Solar + Roof
 * Claim customer got one with nothing above it. Deriving it from the titles
 * instead of from the products is what keeps it right now that Solar does
 * print one: the separator followed the rule rather than a list, so it needed
 * no edit when the rule changed.
 *
 * It also requires its own title, so the flag means what it says on its own
 * rather than only inside the JSX branch that happens to guard it.
 */
export const separatorsBefore = (t: ReportTitles): { roofClaim: boolean } => ({
  roofClaim: t.showRoofClaim && t.showHomeImprovement,
})
